/**
 * The advanced looks: a font, an ink and an effect, chosen together. §6.4.
 *
 * Canva's "Advanced" row, rebuilt from things this editor can actually print.
 * Each preset is an ordinary font from the shop's library - every one a free
 * Google font, fetched into uploads/trimbox-fonts - with an ordinary effect on
 * it. Nothing here draws anything; choosing a preset writes a font, a colour
 * and an effect onto the layer, and the layer draws itself as it always does.
 * Which means a preset, once chosen, prints exactly as reliably as the effect
 * underneath it.
 *
 * The one control a preset offers is Intensity, which moves the effect's main
 * setting along a range chosen to keep the look recognisable at both ends.
 */

import type { Colour, Effect, Ink } from '../model/types';
import { startingEffect } from './plan';

export interface Preset {
  id: string;
  name: string;
  font: { family: string; weight: number };
  fill: Colour;
  /** Set in capitals, when the look depends on it. */
  upper?: boolean;
  /** The effect at a given intensity, 0 to 100. */
  effect: ( intensity: number ) => Effect;
}

const ink = ( ...value: Ink ): Colour => ( { space: 'cmyk', value } );

/** Where an intensity puts a setting, between two ends. */
const along = ( from: number, to: number, intensity: number ) =>
  Math.round( from + ( ( to - from ) * Math.min( 100, Math.max( 0, intensity ) ) ) / 100 );

export const PRESETS: Preset[] = [
  {
    id: 'neon-lights',
    name: 'Neon Lights',
    font: { family: 'Tilt Neon', weight: 400 },
    fill: ink( 0, 0.85, 0, 0 ),
    upper: true,
    effect: ( i ) => ( { ...startingEffect( 'neon', ink( 0, 0.85, 0, 0 ) ), preset: 'neon-lights', size: along( 15, 95, i ) } ),
  },
  {
    id: 'tv-static',
    name: 'TV Static',
    font: { family: 'Rubik Glitch', weight: 400 },
    fill: ink( 0.95, 0.85, 0.2, 0.3 ),
    effect: ( i ) => ( { ...startingEffect( 'glitch', ink( 1, 0, 0, 0 ) ), preset: 'tv-static', offset: along( 8, 60, i ) } ),
  },
  {
    id: 'seventies',
    name: '’70s',
    font: { family: 'Shrikhand', weight: 400 },
    fill: ink( 0, 0.6, 1, 0 ),
    effect: ( i ) => ( {
      ...startingEffect( 'echo', ink( 0, 0.25, 1, 0 ) ),
      preset: 'seventies',
      offset: along( 15, 80, i ),
    } ),
  },
  {
    id: 'sci-fi',
    name: 'Sci-fi',
    font: { family: 'Orbitron', weight: 700 },
    fill: ink( 0.35, 0, 1, 0 ),
    upper: true,
    effect: ( i ) => ( {
      ...startingEffect( 'glow', ink( 0.35, 0, 1, 0 ) ),
      preset: 'sci-fi',
      size: along( 15, 85, i ),
      transparency: 20,
    } ),
  },
  {
    id: 'screenprint',
    name: 'Screenprint',
    font: { family: 'Bungee', weight: 400 },
    fill: ink( 0, 0.95, 0.2, 0.1 ),
    upper: true,
    effect: ( i ) => ( {
      ...startingEffect( 'splice', ink( 0, 0.35, 0.05, 0 ) ),
      preset: 'screenprint',
      offset: along( 10, 60, i ),
      size: 30,
    } ),
  },
  {
    id: 'western',
    name: 'Western',
    font: { family: 'Rye', weight: 400 },
    fill: ink( 0, 0.65, 0.6, 0.05 ),
    effect: ( i ) => ( {
      ...startingEffect( 'drop', ink( 0.3, 0.6, 0.8, 0.4 ) ),
      preset: 'western',
      colour: ink( 0.3, 0.6, 0.8, 0.4 ),
      offset: along( 10, 60, i ),
      blur: 0,
      transparency: 0,
    } ),
  },
  {
    id: 'graffiti',
    name: 'Graffiti',
    font: { family: 'Sedgwick Ave Display', weight: 400 },
    fill: ink( 0.1, 0, 0.35, 0 ),
    effect: ( i ) => ( {
      ...startingEffect( 'outline', ink( 0.85, 0.35, 0.9, 0.45 ) ),
      preset: 'graffiti',
      colour: ink( 0.85, 0.35, 0.9, 0.45 ),
      size: along( 20, 90, i ),
    } ),
  },
  {
    id: 'bubble',
    name: 'Bubble',
    font: { family: 'Rubik Bubbles', weight: 400 },
    fill: ink( 0.75, 0.05, 0.3, 0 ),
    effect: ( i ) => ( {
      ...startingEffect( 'outline', ink( 1, 0.6, 0.2, 0.4 ) ),
      preset: 'bubble',
      colour: ink( 1, 0.6, 0.2, 0.4 ),
      size: along( 15, 70, i ),
    } ),
  },
  {
    id: 'aerobics',
    name: 'Aerobics',
    font: { family: 'Pacifico', weight: 400 },
    fill: ink( 0, 0.35, 0, 0 ),
    effect: ( i ) => ( {
      ...startingEffect( 'glow', ink( 0, 0.85, 0, 0 ) ),
      preset: 'aerobics',
      size: along( 20, 90, i ),
      transparency: 30,
    } ),
  },
  {
    id: 'arcade',
    name: 'Arcade',
    font: { family: 'Press Start 2P', weight: 400 },
    fill: ink( 0.75, 0, 1, 0 ),
    upper: true,
    effect: ( i ) => ( {
      ...startingEffect( 'drop', ink( 0.9, 0.7, 0.6, 0.7 ) ),
      preset: 'arcade',
      colour: ink( 0.9, 0.7, 0.6, 0.7 ),
      offset: along( 10, 70, i ),
      blur: 0,
      transparency: 0,
    } ),
  },
  {
    id: 'cosmic',
    name: 'Cosmic',
    font: { family: 'Great Vibes', weight: 400 },
    fill: ink( 0.3, 0.3, 0, 0 ),
    effect: ( i ) => ( {
      ...startingEffect( 'glow', ink( 0.55, 0.5, 0, 0 ) ),
      preset: 'cosmic',
      size: along( 25, 95, i ),
      transparency: 20,
    } ),
  },
  {
    id: 'pixel',
    name: 'Pixel',
    font: { family: 'Pixelify Sans', weight: 400 },
    fill: ink( 0.95, 0.85, 0, 0.2 ),
    upper: true,
    effect: ( i ) => ( {
      ...startingEffect( 'glitch', ink( 0, 1, 0, 0 ) ),
      preset: 'pixel',
      colour: ink( 0, 1, 0, 0 ),
      colour2: ink( 1, 0, 0, 0 ),
      offset: along( 6, 45, i ),
    } ),
  },
];

export function presetById( id: string | undefined ): Preset | null {
  return PRESETS.find( ( preset ) => preset.id === id ) ?? null;
}

/**
 * Where on its intensity scale an effect sits, worked back from the effect.
 *
 * The intensity is not stored - only the effect is, which is what prints - so
 * the slider finds its place by asking which intensity would have produced
 * the setting it moves.
 */
export function intensityOf( preset: Preset, effect: Effect ): number {
  let best = 50;
  let gap = Infinity;

  for ( let i = 0; i <= 100; i++ ) {
    const made = preset.effect( i );
    const d =
      Math.abs( made.size - effect.size ) + Math.abs( made.offset - effect.offset ) + Math.abs( made.blur - effect.blur );

    if ( d < gap ) {
      gap = d;
      best = i;
    }
  }

  return best;
}
